'use client';

import { MediaError, MediaProcessingStatus } from '@/lib/types/media/core';
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils';
;
import { memo } from 'react';

export interface UploadProgressProps {
  progress: number;
  status: MediaProcessingStatus;
  error?: MediaError | null;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export const UploadProgress = memo(function UploadProgress({
  progress,
  status,
  error,
  className,
  size = 'md',
  showLabel = true
}: UploadProgressProps) {
  // Size configurations
  const sizeConfig = {
    sm: {
      circleSize: 'w-12 h-12',
      iconSize: 'w-6 h-6',
      textSize: 'text-xs',
      strokeWidth: 6
    },
    md: {
      circleSize: 'w-16 h-16',
      iconSize: 'w-8 h-8',
      textSize: 'text-sm',
      strokeWidth: 8
    },
    lg: {
      circleSize: 'w-20 h-20',
      iconSize: 'w-10 h-10',
      textSize: 'text-base',
      strokeWidth: 8
    }
  };

  const { circleSize, iconSize, textSize, strokeWidth } = sizeConfig[size];
  const percentage = Math.round(progress * 100);

  if (error || status === 'error') {
    return (
      <div className={cn("flex flex-col items-center gap-2", className)}>
        <Icon icon='alert-triangle' className={cn(iconSize, "text-danger animate-pulse")}  />
        {showLabel && (
          <span className="text-small text-danger font-medium">
            {error?.message || 'Upload failed'}
          </span>
        )}
      </div>
    );
  }

  if (status === 'processing') {
    return (
      <div className={cn("flex flex-col items-center gap-2", className)}>
        <div className="relative">
          <Icon icon='spinner' className={cn(iconSize, "text-primary animate-spin")}  />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={cn(textSize, "font-medium")}>100%</span>
          </div>
        </div>
        {showLabel && (
          <div className="flex flex-col items-center">
            <span className="text-small font-medium">Processing...</span>
            <span className="text-xs text-muted-foreground">Almost done!</span>
          </div>
        )}
      </div>
    );
  }

  if (status === 'uploading') {
    // Calculate circle parameters
    const radius = size === 'sm' ? 20 : size === 'md' ? 28 : 36;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference - (percentage / 100) * circumference;

    return (
      <div className={cn("flex flex-col items-center gap-3", className)}>
        <div className={cn("relative", circleSize)}>
          {/* Circular progress track */}
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            {/* Background circle */}
            <circle
              className={cn("text-muted", className)}
              strokeWidth={strokeWidth}
              stroke="currentColor"
              fill="none"
              r={radius}
              cx="50"
              cy="50"
            />
            {/* Progress circle */}
            <circle
              className={cn("text-primary transition-all duration-300", className)}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
              stroke="currentColor"
              fill="none"
              r={radius}
              cx="50"
              cy="50"
            />
          </svg>

          {/* Centered percentage */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={cn(textSize, "font-medium")}>
              {percentage}%
            </span>
          </div>
        </div>

        {showLabel && (
          <div className="flex flex-col items-center">
            <span className="text-small font-medium">Uploading...</span>
            <span className="text-xs text-muted-foreground">Please wait</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      <Icon icon='check-circle' className={cn(iconSize, "text-success")}  />
      {showLabel && (
        <span className="text-small font-medium">Complete</span>
      )}
    </div>
  );
});
